import { DataContext } from '@/types';
import { Data } from '../Data';
import { SettingsCourse } from './SettingCourse';

/**
 * The OLC catalogue courses a competency can be linked to.
 *
 * Separate from SettingsCourseList because /Integration/Courses cannot return catalogue rows at all:
 * it resolves an OrganizationIntegration and guards on it, so the synthetic catalogue provider id is a
 * guaranteed IntegrationNotFound, and it filters in memory over IntegrationCourse entities, which
 * catalogue courses are not.
 *
 * Each course carries this organisation's recurrence, which the editor needs: it already marks a
 * course invalid, renders it as an error chip and auto-toggles the expiry switch based on that value.
 */
export class SettingsCatalogueCourseList extends Data {
  courses: SettingsCourse[];

  constructor(options: any) {
    const { searchText, attributeDefinitionId, ...otherOptions } = options;

    const queryParams = [];
    if (searchText) queryParams.push(`searchText=${encodeURIComponent(searchText)}`);
    // Sending the competency being edited keeps its already-linked courses in the list even when they
    // no longer appear in the catalogue listing — otherwise a de-listed course silently drops out of
    // the picker and the next save (which sends the complete desired set) deletes a link the admin was
    // never shown.
    if (attributeDefinitionId) queryParams.push(`attributeDefinitionId=${attributeDefinitionId}`);

    super({
      api: `/AttributeDefinition/CatalogueCourses${queryParams.length ? `?${queryParams.join('&')}` : ''}`,
      callback: () => options.resolve(this, false),
      ...otherOptions,
    });

    this.courses = [];
  }

  onDataReceived(data: any) {
    (data || []).forEach((course: any) => {
      const settingsCourse = new SettingsCourse(course);
      settingsCourse.isCatalogue = true;
      this.courses.push(settingsCourse);
    });
  }

  static load(
    context: DataContext,
    options: { searchText?: string; attributeDefinitionId?: string }
  ): Promise<SettingsCatalogueCourseList> {
    return new Promise((resolve, reject) => {
      new SettingsCatalogueCourseList({
        searchText: options.searchText,
        attributeDefinitionId: options.attributeDefinitionId,
        resolve,
        reject,
        context,
      });
    });
  }
}
